import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

/**
 * Table Name In Local Storage
 *
 * @param {object} tablePreferences - The table preferences.
 * @param {string} tableId - The table id.
 * @returns {boolean} Whether the table name is in local storage.
 */
export const tableNameInLocalStorage = (tablePreferences, tableId) => {
  const { [tableId]: tableName } = tablePreferences;
  return !!tableName;
};

const constructHeader = (headerList, h) =>
  headerList ? `${headerList}.${h}` : h;
const getRowData = (
  d,
  headers,
  headersReWrite,
  canReWriteHeaders,
  row = [],
  headerList = null,
) => {
  if (canReWriteHeaders) {
    headers.forEach((h) => {
      if (h !== '__typename') {
        if (typeof d[h] === 'object' && d[h]) {
          const rowHeaders = Object.keys(d[h]);
          const newHeader = constructHeader(headerList, h);
          getRowData(
            d[h],
            rowHeaders,
            headersReWrite,
            canReWriteHeaders,
            row,
            newHeader,
          );
        } else {
          const newHeader = constructHeader(headerList, h);
          const rowData = d[h] || '';
          row.push(rowData);
          headersReWrite.push(newHeader);
        }
      }
    });
  } else {
    headers.forEach((h) => {
      if (h !== '__typename') {
        if (typeof d[h] === 'object' && d[h]) {
          const rowHeaders = Object.keys(d[h]);
          getRowData(d[h], rowHeaders, headersReWrite, canReWriteHeaders, row);
        } else {
          const rowData = d[h] || '';
          row.push(rowData);
        }
      }
    });
  }
  return row;
};
/**
 * Get Export Array Util
 *
 * @param {Array} downloadAccessor - The download accessor.
 * @param {boolean} multiSheet - Whether the export is multi sheet.
 * @param {Function} getSheetName - The get sheet name function.
 * @returns {Array} The export array.
 */
export const getExportArrayUtil = (
  downloadAccessor = [],
  multiSheet = false,
  getSheetName = null,
) => {
  // Converting json into a string array.
  const exportRes = [];
  let headers = [];
  let col = [];
  const sheets = [];

  if (downloadAccessor.length > 0) {
    if (multiSheet) {
      if (!getSheetName) {
        throw Error(
          'getSheetName is not defined. This is required when multiSheet is enabled',
        );
      }
      downloadAccessor.forEach((d) => {
        const SheetHeaders = Object.keys(d);
        const sheet = [];
        SheetHeaders.forEach((header) => {
          col = [];
          if (d[header] && header !== '__typename') {
            if (typeof d[header] === 'object') {
              if (d[header].length) {
                headers = Object.keys(d[header][0]);
                const headersReWrite = [];
                d[header].forEach((dH) => {
                  const row = getRowData(
                    dH,
                    headers,
                    headersReWrite,
                    headersReWrite.length === 0,
                  );
                  col.push(row);
                });
                const result = [];
                result.push([header]);
                result.push(headersReWrite);
                sheet.push(result.concat(col));
                sheet.push(['']);
              } else {
                headers = Object.keys(d[header]);
                const headersReWrite = [];
                const row = getRowData(
                  d[header],
                  headers,
                  headersReWrite,
                  headersReWrite.length === 0,
                );
                const result = [];
                result.push([header]);
                result.push(headersReWrite);
                result.push(row);
                result.push('');
                sheet.push(result);
              }
            } else {
              // This is to cater if the key is not an array or an object.
              const result = [];
              result.push([header]);
              result.push(d[header]);
              result.push('');
              sheet.push(result);
            }
          } else if (header !== '__typename') {
            sheet.push([[header]]);
            sheet.push([['No Data To Display']]);
            sheet.push([['']]);
          }
        });

        let concattedSheet = [];

        sheet.forEach((s) => {
          concattedSheet = concattedSheet.concat(s);
        });

        sheets.push({
          data: concattedSheet,
          name: getSheetName(d),
        });
      });
      return sheets;
    }
    headers = Object.keys(downloadAccessor[0]);
    const headersReWrite = [];
    downloadAccessor.forEach((d) => {
      const row = getRowData(
        d,
        headers,
        headersReWrite,
        headersReWrite.length === 0,
      );
      col.push(row);
    });

    exportRes.push(headersReWrite);
    return exportRes.concat(col);
  }
  return [];
};
/**
 * Refetch Data
 * @param {object} props - The component props.
 * @param {object} props.variables - The variables.
 * @param {Function} props.refetch - The refetch function.
 * @param {number} props.pageIndex - The page index.
 * @param {number} props.pageSize - The page size.
 * @param {Array} props.sortBy - The sort by.
 * @param {string} props.tableId - The table id.
 * @returns {void}
 */
export const refetchData = ({
  variables,
  refetch,
  pageIndex,
  pageSize,
  sortBy,
  tableId,
}) => {
  const tempVariables = {
    limit: pageSize,
    skip: pageIndex * pageSize,
    search: '',
    filter: '',
    id: '',
  };
  if (sortBy.length > 0) {
    tempVariables.sort = `${sortBy[0].id}:${sortBy[0].desc ? 'DESC' : 'ASC'}`;
  }

  if (variables.filter) {
    const keys = Object.keys(variables.filter);
    const filters = keys
      .map(
        (key) =>
          `filter=${key}${variables.filter[key].type}=${variables.filter[key].value}`,
      )
      .join('&');
    tempVariables.filter = filters;
  }

  if (variables.search) {
    tempVariables.search = variables.search;
  }
  if (variables.id) {
    tempVariables.id = variables.id;
  }
  const query = [];
  if (tempVariables) {
    if (tempVariables.limit) {
      query.push(`limit=${tempVariables.limit}`);
    }
    if (tempVariables.skip) {
      query.push(`skip=${tempVariables.skip}`);
    }
    if (tempVariables.sort) {
      query.push(`sort=${tempVariables.sort}`);
    }
    if (tempVariables.search) {
      query.push(`search=${tempVariables.search}`);
    }
    if (tempVariables.filter) {
      query.push(`${tempVariables.filter}`);
    }
  }

  const tablePreferences = localStorage.getItem('tablePreferences');
  const preferences = tablePreferences ? JSON.parse(tablePreferences) : {};
  if (tableNameInLocalStorage(preferences, tableId)) {
    preferences[tableId] = {
      ...preferences[tableId],
      query,
    };
    localStorage.setItem('tablePreferences', JSON.stringify(preferences));
  } else {
    preferences[tableId] = {
      query,
    };
    localStorage.setItem('tablePreferences', JSON.stringify(preferences));
  }

  if (refetch) {
    if (tempVariables.id) {
      refetch(tempVariables.id, query.join('&'));
    } else {
      refetch(query.join('&'));
    }
  }
};

/**
 * Build the data needed for the pagination of the table.
 *
 * @param {PaginationData} data - Information needed to build pagination data.
 * @returns {object} The pagination data.
 */
export const getPaginationData = ({ dataCount, pageIndex, pageSize }) => {
  const currentDataCount = Math.min(dataCount, (pageIndex + 1) * pageSize);
  let lastPage = Math.floor(dataCount / pageSize);
  if (lastPage * pageSize === dataCount) {
    lastPage -= 1;
  }
  return {
    canNext: (pageIndex + 1) * pageSize < dataCount,
    canPrevious: pageIndex !== 0,
    paginationText: `Showing ${dataCount === 0 ? 0 : pageIndex * pageSize + 1} - ${currentDataCount} of ${dataCount}`,
    lastPage,
  };
};
const getFilterData = (filterInfo, filterData) => {
  let retVal = null;
  switch (filterInfo.type) {
    case 'daterange':
      if (filterData.startDate && filterData.endDate) {
        retVal = {};
        const startDate = filterData.startDate
          .utcOffset(0)
          .set({
            hour: 0,
            minute: 0,
            second: 1,
            millisecond: 0,
          })
          .toISOString();
        const endDate = filterData.endDate
          .utcOffset(0)
          .set({
            hour: 23,
            minute: 59,
            second: 59,
            millisecond: 0,
          })
          .toISOString();
        retVal[`${filterInfo.accessor}`] = {
          type: '$btw',
          value: `${startDate}, ${endDate}`,
        };
        return retVal;
      }
      return retVal;
    case 'date':
      if (filterData.date) {
        retVal = {};
        const { date } = filterData;
        retVal[`${filterInfo.accessor}`] = {
          type: '$gte',
          value: date
            .utcOffset(0)
            .set({
              hour: 0,
              minute: 0,
              second: 1,
              millisecond: 0,
            })
            .toISOString(),
        };
        return retVal;
      }
      return retVal;
    case 'datetimerange':
      if (filterData.startDate && filterData.endDate) {
        retVal = {};
        const { startDate, endDate } = filterData;
        retVal[`${filterInfo.accessor}`] = {
          type: '$btw',
          value: `${startDate}, ${endDate}`,
        };
        return retVal;
      }
      return retVal;
    case 'select':
      if (filterData.value) {
        retVal = {};
        let { value } = filterData;
        if (filterData.value === 'true' || filterData.value === 'false') {
          value = filterData.value === 'true';
        }
        retVal[filterInfo.accessor] = {
          type: filterInfo.filterType || '$eq',
          value,
        };
      }
      return retVal;
    case 'multiselect':
      if (filterData.value) {
        retVal = {};
        const { value } = filterData;
        if (value.length > 0) {
          retVal[filterInfo.accessor] = {
            type: '$in',
            value: value.map((val) => val.value).join(','),
          };
        }
      }
      return retVal;
    default:
      return null;
  }
};
/**
 * Get Search Data
 * @param {Array} headers - The headers.
 * @param {string} value - The value.
 * @returns {Array} The search data.
 */
export const getSearchData = (headers, value) => {
  const retVal = [];
  headers.forEach((h) => {
    if (h.search) {
      retVal.push({
        key: h.accessor,
        value,
      });
    }
  });
  return retVal;
};

/**
 * Get Apply Filter
 *
 * @param {object} filterValues - The filter values.
 * @param {Array} filters - The filters.
 * @param {object} variables - The variables.
 * @param {object} defaultVariables - The default variables.
 * @returns {object} The apply filter.
 */
export const getApplyFilter = (
  filterValues,
  filters,
  variables,
  defaultVariables,
) => {
  const getQueryVariables = JSON.parse(JSON.stringify(variables));
  let newFilters = {};
  if (defaultVariables && defaultVariables.filter) {
    newFilters = defaultVariables.filter;
  }

  let addedFilters = false;
  const retVal = {};
  filters.forEach((f) => {
    let filter;
    if (f.customFilter) {
      filter = f.customFilter(filterValues[f.accessor]);
    } else {
      filter = getFilterData(f, filterValues[f.accessor]);
    }
    if (filter) {
      newFilters = {
        ...newFilters,
        ...filter,
      };
      addedFilters = true;
    }
  });

  if (addedFilters) {
    retVal.variables = {
      ...getQueryVariables,
      filter: newFilters,
    };
  }
  return retVal;
};

/**
 * Download XLSX
 *
 * @param {Array} data - The data.
 * @param {string} downloadName - The download name.
 * @param {Function} downloadComplete - The download complete function.
 * @returns {void}
 */
export const downloadXLSX = (data, downloadName, downloadComplete) => {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(data);
  XLSX.utils.book_append_sheet(
    wb,
    ws,
    `${downloadName}${dayjs().format('YYYYMMDD')}`,
  );
  XLSX.writeFile(wb, `${downloadName}-${dayjs().format('YYYYMMDD')}.csv`);
  if (downloadComplete) {
    downloadComplete();
  }
};

export default {
  refetchData,
  getPaginationData,
  getSearchData,
  getExportArrayUtil,
};
/**
 * @typedef PaginationData
 * @property {string} type - The type of table we need to render pagination for.
 * @property {number} maxPageCount - Custom. The max amount of pages that there
 * are for the page size.
 * @property {number} dataCount - Prisma. The total amount of rows that the table contains.
 * @property {number} pageSize - Both. The total number of rows to show per page.
 * @property {number} pageIndex - Both. What page is the user currently viewing.
 */
